import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import logo from './assets/logo.png';
import UploadForm from './components/UploadForm';
import WrmdForm from './components/WrmdForm';

// ── Step indicator ────────────────────────────────────────────────────────────
const STEPS = ['Upload Photo', 'Review & Submit'];

function StepIndicator({ currentStep }) {
  const stepIndex = { upload: 0, form: 1 }[currentStep] ?? 0;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0, mb: 4 }}>
      {STEPS.map((label, i) => {
        const done   = i < stepIndex;
        const active = i === stepIndex;
        return (
          <React.Fragment key={label}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: done ? 'primary.light' : active ? 'primary.main' : '#EDE7D5',
                  color: done || active ? '#fff' : 'text.disabled',
                  fontWeight: 700, fontSize: '0.75rem',
                  border: active ? '2px solid' : '2px solid transparent',
                  borderColor: active ? 'primary.dark' : 'transparent',
                  transition: 'all 0.25s ease',
                }}
              >
                {done ? '✓' : i + 1}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: active ? 700 : 500,
                  color: active ? 'primary.main' : done ? 'primary.light' : 'text.disabled',
                  fontSize: '0.65rem', letterSpacing: '0.03em', whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
            </Box>
            {i < STEPS.length - 1 && (
              <Box
                sx={{
                  width: 40, height: 2,
                  bgcolor: i < stepIndex ? 'primary.light' : '#DDD5C0',
                  mx: 0.5, mb: 1.8, borderRadius: 1,
                  transition: 'background-color 0.25s ease',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [step, setStep] = useState('upload'); // 'upload' | 'form'
  const [extractedData, setExtractedData] = useState(null);
  const [warnings, setWarnings] = useState([]);

  const handleExtracted = (data, warns = []) => {
    setExtractedData(data);
    setWarnings(warns);
    setStep('form');
  };

  const handleBack = () => {
    setExtractedData(null);
    setWarnings([]);
    setStep('upload');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: '#FDFAF4', borderBottom: '1px solid #DDD5C0',
          boxShadow: '0 1px 6px rgba(60,50,20,0.07)',
          py: { xs: 2.5, sm: 3 }, px: 2,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 2.5 } }}>
            <Box
              component="img"
              src={logo}
              alt="SERC — Southeastern Reptile Conservation"
              sx={{
                height: { xs: 64, sm: 80 }, width: { xs: 64, sm: 80 },
                borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
                boxShadow: '0 2px 12px rgba(60,50,20,0.18)', border: '2px solid #DDD5C0',
              }}
            />
            <Box>
              <Typography
                variant="h5" fontWeight={800}
                sx={{ color: 'primary.dark', fontSize: { xs: '1.15rem', sm: '1.35rem' }, lineHeight: 1.2 }}
              >
                Wildlife Intake
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontSize: { xs: '0.78rem', sm: '0.85rem' }, fontWeight: 500, mt: 0.3 }}
              >
                SERC
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'secondary.main', fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}
              >
                Protecting Native Species
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <Container maxWidth="md">
        <Box sx={{ pt: 4, pb: 8 }}>
          <StepIndicator currentStep={step} />

          {step === 'upload' && (
            <UploadForm onExtracted={handleExtracted} />
          )}

          {step === 'form' && (
            <WrmdForm
              data={extractedData}
              warnings={warnings}
              onBack={handleBack}
            />
          )}
        </Box>
      </Container>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <Box
        component="footer"
        sx={{ borderTop: '1px solid #DDD5C0', py: 2, px: 2, textAlign: 'center', bgcolor: '#FDFAF4' }}
      >
        <Typography variant="caption" color="text.disabled">
          © SERC · Southeastern Reptile Conservation · Licensed Wildlife Rehabilitators · Virginia
        </Typography>
      </Box>
    </Box>
  );
}
